import { useEffect, useRef, useState } from 'react';
import { Calendar, ChevronDown, Copy, ExternalLink, Info } from 'lucide-react';
import { Button, ScrollArea, Separator, Tabs, TabsContent, TabsList, TabsTrigger, cn } from 'shared-ui';

import { FindAndFixWordmark } from '@/components/shared/FindAndFixWordmark';
import { PanelCloseButton, SlidingPanel } from '@/components/shared/SlidingPanel';
import { StatusPill } from '@/components/shared/StatusPill';
import { getPoiStatusPillPresentation } from '@/components/shared/statusPillPresentation';
import type { PoiHistoryEvent } from '@/api/poiHistoryApi';
import type { PoiPipelinesState } from '@/hooks/usePoiPipelines';
import { PoiHistoryTab } from './poi-history/PoiHistoryTab';
import { PoiPipelinesTab } from './poi-pipelines/PoiPipelinesTab';
import { PoiHistoryEventDetail } from './poi-history/PoiHistoryEventDetail';
import {
    formatPipeType,
    formatRepaired,
    formatSearchDate,
    getLeakageRateDisplay,
    formatLeakageRateMlD,
} from '../utils/poiFeatureProperties';
import type { PoiFeatureProperties } from '../utils/poiFeatureProperties';
import { buildPoiClipboardText } from '../utils/poiClipboard';
import { PoiPanelField, PoiWhat3WordsField } from './PoiPanelFields';

type PoiPipelinesTabState = PoiPipelinesState & { retry: () => void };

// Keeps the panel renderable without a pipelines feed (tests, future embedders): the tab
// simply shows nothing beyond the banner until real state is supplied.
const IDLE_PIPELINES: PoiPipelinesTabState = { data: null, isLoading: false, isError: false, retry: () => undefined };

interface PoiDetailsPanelProps {
    poi: PoiFeatureProperties | null;
    poiCoords?: [number, number] | null;
    /**
     * The client the POI belongs to, for the per-record reads (history). The selected client
     * in Find & Fix; in the Network Explorer the record's own client, since records of several
     * clients share one map.
     */
    clientId?: string | null;
    isOpen: boolean;
    onClose: () => void;
    onPrevious?: () => void;
    onNext?: () => void;
    canNavigate?: boolean;
    /**
     * Leave out the worklist Previous/Next controls. Worklist navigation is a single-client
     * idea, so the Network Explorer's read-only view has none.
     */
    hideNavigation?: boolean;
    /** When present, the footer offers to open this record in its client's Find & Fix. */
    onOpenInFindAndFix?: () => void;
    /**
     * The associated-pipelines state for the selected POI, owned by MapView (the same
     * response drives the map's radius display, so the fetch lives with the selection).
     */
    pipelines?: PoiPipelinesTabState;
}

type CopyState = 'idle' | 'copied' | 'failed';

function PanelContent({
    poi,
    poiCoords,
    clientId,
    onClose,
    onPrevious,
    onNext,
    canNavigate,
    hideNavigation,
    onOpenInFindAndFix,
    pipelines,
}: {
    poi: PoiFeatureProperties;
    poiCoords?: [number, number] | null;
    clientId: string | null;
    onClose: () => void;
    onPrevious?: () => void;
    onNext?: () => void;
    canNavigate?: boolean;
    hideNavigation?: boolean;
    onOpenInFindAndFix?: () => void;
    pipelines: PoiPipelinesTabState;
}) {
    const leakage = getLeakageRateDisplay(poi);
    const pipeType = formatPipeType(poi.pipe_type);
    const formattedSurveyedDate = formatSearchDate(poi.surveyed_date);
    const formattedDate = formatSearchDate(poi.search_date);
    const formattedRepaired = formatRepaired(poi.repaired);

    const [copyState, setCopyState] = useState<CopyState>('idle');
    const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Controlled tabs: the sticky hasActivatedHistory flag drives the lazy history fetch
    // (nothing loads until the user first opens the History tab for this POI).
    const [activeTab, setActiveTab] = useState('details');
    const [hasActivatedHistory, setHasActivatedHistory] = useState(false);

    // A selected timeline event replaces the tabs area with its detail view. The tabs
    // (and the timeline's scroll position) stay mounted underneath, just hidden, so Back
    // restores the History tab exactly as the user left it.
    const [detailEvent, setDetailEvent] = useState<PoiHistoryEvent | null>(null);

    // Reset the lazy-fetch flag and any open event detail when the selected POI changes
    // (render-time derived state, same idiom as the lastPoi tracking in PoiDetailsPanel
    // below). The active tab is kept so Previous/Next browsing stays on whichever tab
    // the user was reading.
    const [prevPoiId, setPrevPoiId] = useState(poi.id);
    if (poi.id !== prevPoiId) {
        setPrevPoiId(poi.id);
        setHasActivatedHistory(activeTab === 'history');
        setDetailEvent(null);
    }

    useEffect(() => {
        return () => {
            if (resetTimerRef.current !== null) clearTimeout(resetTimerRef.current);
        };
    }, []);

    const scheduleReset = () => {
        if (resetTimerRef.current !== null) clearTimeout(resetTimerRef.current);
        resetTimerRef.current = setTimeout(() => {
            resetTimerRef.current = null;
            setCopyState('idle');
        }, 1500);
    };

    const handleCopy = async () => {
        const text = buildPoiClipboardText(poi, poiCoords);
        try {
            await navigator.clipboard.writeText(text);
            setCopyState('copied');
        } catch {
            // Covers both permission denial and clipboard API being unavailable at runtime.
            setCopyState('failed');
        }
        scheduleReset();
    };

    return (
        <>
            <div className="flex items-start justify-between px-6 pt-6 pb-3">
                <div className="space-y-2">
                    <h2 className="text-xl font-black uppercase">Point of Interest</h2>
                    {/* Nothing in the explorer names a record's owning client: not this header
                        and not the hover card (design decision). */}
                    <StatusPill presentation={getPoiStatusPillPresentation(poi.status)} />
                </div>
                <PanelCloseButton onClick={onClose} aria-label="Close POI details" />
            </div>
            <Separator />
            <Tabs
                value={activeTab}
                onValueChange={(value) => {
                    setActiveTab(String(value));
                    if (value === 'history') setHasActivatedHistory(true);
                }}
                className={cn('min-h-0 flex-1 flex-col', detailEvent === null ? 'flex' : 'hidden')}
            >
                <TabsList variant="line" className="poi-panel-tabs-list w-full rounded-none border-b p-0">
                    <TabsTrigger value="details" className="text-primary data-active:text-primary flex-1">
                        Details
                    </TabsTrigger>
                    <TabsTrigger value="pipelines" className="text-primary data-active:text-primary flex-1">
                        Pipelines
                    </TabsTrigger>
                    <TabsTrigger value="history" className="text-primary data-active:text-primary flex-1">
                        History
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="mt-0 flex min-h-0 flex-1 flex-col">
                    <ScrollArea className="min-h-0 flex-1">
                        <div className="space-y-4 px-6 py-4">
                            <PoiPanelField label="Worklist">
                                <span className="flex-1 text-sm">{poi.name}</span>
                            </PoiPanelField>
                            <PoiPanelField label="Stop">
                                <span className="flex-1 text-sm">{poi.stop}</span>
                            </PoiPanelField>
                            <PoiWhat3WordsField
                                label="Point of Interest What3words"
                                meta="(Original)"
                                value={poi.what3words}
                            />
                            <PoiWhat3WordsField label="Leak What3words" meta="(Actual)" value={poi.leak_what3words} />
                            <PoiPanelField label="Pipe Type">
                                <Info className="text-primary size-4 shrink-0 stroke-[1.5]" aria-hidden="true" />
                                <span className="flex-1 text-sm">{pipeType ?? '—'}</span>
                                <ChevronDown className="poi-panel-control__trailing size-4" aria-hidden="true" />
                            </PoiPanelField>
                            <PoiPanelField label="Leakage Rate" meta={`(${leakage.provenance})`}>
                                <span className="poi-panel-control__unit">Ml/D</span>
                                <Separator orientation="vertical" aria-hidden="true" />
                                <span className="flex-1 text-sm">{formatLeakageRateMlD(leakage.value)}</span>
                                <ChevronDown className="poi-panel-control__trailing size-4" aria-hidden="true" />
                            </PoiPanelField>
                            {/* The value is poi.surveyed_date, so the label says so — this
                                field previously mislabelled it "Date Created". */}
                            <PoiPanelField label="Surveyed Date">
                                <span
                                    className={cn('flex-1 text-sm', !formattedSurveyedDate && 'text-muted-foreground')}
                                >
                                    {formattedSurveyedDate ?? 'DD/MM/YYYY'}
                                </span>
                                <Calendar className="poi-panel-control__trailing size-4" aria-hidden="true" />
                            </PoiPanelField>
                            <PoiPanelField label="Date of Search">
                                <span className={cn('flex-1 text-sm', !formattedDate && 'text-muted-foreground')}>
                                    {formattedDate ?? 'DD/MM/YYYY'}
                                </span>
                                <Calendar className="poi-panel-control__trailing size-4" aria-hidden="true" />
                            </PoiPanelField>
                            <PoiPanelField label="Repaired?">
                                <span className="flex-1 text-sm">{formattedRepaired ?? '—'}</span>
                                <ChevronDown className="poi-panel-control__trailing size-4" aria-hidden="true" />
                            </PoiPanelField>
                        </div>
                    </ScrollArea>
                    <div className="shrink-0 space-y-2 border-t px-6 py-4">
                        <Button
                            className="h-10 w-full cursor-pointer"
                            type="button"
                            onClick={() => {
                                void handleCopy();
                            }}
                            aria-label={
                                copyState === 'copied'
                                    ? 'Copied to clipboard'
                                    : copyState === 'failed'
                                      ? 'Copy failed'
                                      : 'Copy POI details to clipboard'
                            }
                        >
                            <Copy className="size-4" aria-hidden="true" />
                            {copyState === 'copied'
                                ? 'Copied'
                                : copyState === 'failed'
                                  ? 'Copy Failed'
                                  : 'Copy To Clipboard'}
                        </Button>
                        {onOpenInFindAndFix && (
                            <Button
                                variant="outline"
                                type="button"
                                className="h-10 w-full cursor-pointer"
                                onClick={onOpenInFindAndFix}
                            >
                                <ExternalLink className="size-4" aria-hidden="true" />
                                {/* One inline run, so "Open in" and the wordmark share a baseline. */}
                                <span>
                                    Open in <FindAndFixWordmark />
                                </span>
                            </Button>
                        )}
                        {!hideNavigation && (
                            <div className="grid grid-cols-2 gap-2">
                                <Button
                                    variant="ghost"
                                    type="button"
                                    className="panel-close-btn text-primary h-10 cursor-pointer"
                                    onClick={onPrevious}
                                    disabled={!canNavigate || !onPrevious}
                                >
                                    Previous
                                </Button>
                                <Button
                                    variant="ghost"
                                    type="button"
                                    className="panel-close-btn text-primary h-10 cursor-pointer"
                                    onClick={onNext}
                                    disabled={!canNavigate || !onNext}
                                >
                                    Next
                                </Button>
                            </div>
                        )}
                    </div>
                </TabsContent>

                <TabsContent value="pipelines" className="mt-0 flex min-h-0 flex-1 flex-col">
                    {/* No lazy gate: the fetch fires on selection because the map's radius
                        display needs the same response immediately. */}
                    <PoiPipelinesTab state={pipelines} />
                </TabsContent>

                <TabsContent value="history" className="mt-0 flex min-h-0 flex-1 flex-col">
                    <PoiHistoryTab
                        poiId={poi.id}
                        clientId={clientId}
                        enabled={hasActivatedHistory}
                        onViewDetails={setDetailEvent}
                    />
                </TabsContent>
            </Tabs>

            {detailEvent !== null && (
                <PoiHistoryEventDetail
                    event={detailEvent}
                    onBack={() => {
                        setDetailEvent(null);
                    }}
                />
            )}
        </>
    );
}

export function PoiDetailsPanel({
    poi,
    poiCoords,
    clientId = null,
    isOpen,
    onClose,
    onPrevious,
    onNext,
    canNavigate,
    hideNavigation = false,
    onOpenInFindAndFix,
    pipelines = IDLE_PIPELINES,
}: PoiDetailsPanelProps) {
    const [lastPoi, setLastPoi] = useState<PoiFeatureProperties | null>(poi ?? null);
    const [prevPoi, setPrevPoi] = useState(poi);
    if (poi !== prevPoi) {
        setPrevPoi(poi);
        if (poi !== null) setLastPoi(poi);
    }

    return (
        <SlidingPanel
            isOpen={isOpen}
            onClose={onClose}
            ariaLabel="POI details"
            title="Point of Interest Details"
            widthClassName="w-[390px]"
        >
            {lastPoi && (
                <PanelContent
                    poi={lastPoi}
                    poiCoords={poiCoords}
                    clientId={clientId}
                    onClose={onClose}
                    onPrevious={onPrevious}
                    onNext={onNext}
                    canNavigate={canNavigate}
                    hideNavigation={hideNavigation}
                    onOpenInFindAndFix={onOpenInFindAndFix}
                    pipelines={pipelines}
                />
            )}
        </SlidingPanel>
    );
}
